import { clsx } from "clsx";
import moment from "moment";
import { twMerge } from "tailwind-merge";

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

/**
 * This function was adapted from the one in the ReadMe of https://github.com/DominicTobias/react-image-crop
 */

export const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", (error) => reject(error));
    image.setAttribute("crossOrigin", "anonymous"); // needed to avoid cross-origin issues on CodeSandbox
    image.src = url;
  });

export function getRadianAngle(degreeValue) {
  return (degreeValue * Math.PI) / 180;
}

/**
 * Returns the new bounding area of a rotated rectangle.
 */
export function rotateSize(width, height, rotation) {
  const rotRad = getRadianAngle(rotation);

  return {
    width:
      Math.abs(Math.cos(rotRad) * width) + Math.abs(Math.sin(rotRad) * height),
    height:
      Math.abs(Math.sin(rotRad) * width) + Math.abs(Math.cos(rotRad) * height),
  };
}

export default async function getCroppedImg(
  imageSrc,
  pixelCrop,
  rotation = 0,
  flip,
  fileName = "cropped-image.png",
) {
  const image = await createImage(imageSrc);
  const canvas = document.createElement("canvas");
  const ctx = canvas.getContext("2d");

  if (!ctx) {
    return null;
  }

  const rotRad = getRadianAngle(rotation);

  // calculate bounding box of the rotated image
  const { width: bBoxWidth, height: bBoxHeight } = rotateSize(
    image.width,
    image.height,
    rotation,
  );

  // set canvas size to match the bounding box
  canvas.width = bBoxWidth;
  canvas.height = bBoxHeight;

  // translate canvas context to a central location to allow rotating and flipping around the center
  ctx.translate(bBoxWidth / 2, bBoxHeight / 2);
  ctx.rotate(rotRad);
  ctx.scale(flip?.horizontal ? -1 : 1, flip?.vertical ? -1 : 1);
  ctx.translate(-image.width / 2, -image.height / 2);

  // draw rotated image
  ctx.drawImage(image, 0, 0);

  const croppedCanvas = document.createElement("canvas");
  const croppedCtx = croppedCanvas.getContext("2d");

  if (!croppedCtx) {
    return null;
  }

  // Set the size of the cropped canvas
  croppedCanvas.width = pixelCrop.width;
  croppedCanvas.height = pixelCrop.height;

  // Draw the cropped image onto the new canvas
  croppedCtx.drawImage(
    canvas,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  // // As a blob
  // return new Promise((resolve, reject) => {
  //   croppedCanvas.toBlob((file) => {
  //     if (file) {
  //       resolve(URL.createObjectURL(file));
  //     } else {
  //       reject(new Error("Canvas is empty"));
  //     }
  //   }, "image/png");
  // });

  return new Promise((resolve, reject) => {
    croppedCanvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], fileName, {
          type: "image/png",
          lastModified: Date.now(),
        });
        resolve({
          fileData: file,
          fileUrl: URL.createObjectURL(blob),
        }); // ✅ actual binary Blob file
      } else {
        reject(new Error("Canvas is empty"));
      }
    }, "image/png");
  });
}

export const normalizeVerificationData = (
  data = {},
  verificationType = {},
  selectedTypeId,
  setVerificationTypes,
) => {
  if (!verificationType?.fields) return data;

  const normalizedData = { ...data };
  const fieldTypeMap = {};

  // Default types
  verificationType.fields.forEach((field) => {
    fieldTypeMap[field.key] = field.inputType || "text";
  });

  // Evaluate conditions
  verificationType.fields.forEach((field) => {
    if (!field.condition) return;

    const { key, type, default: defaultType, condition } = field.condition;

    const value = normalizedData[field.key];

    // eslint-disable-next-line no-new-func
    const isMatched = new Function(
      "value",
      `return ${condition.replace(/`/g, "")}`,
    )(value);

    fieldTypeMap[key] = isMatched ? type : defaultType;
  });

  // Update verificationTypes state
  setVerificationTypes && setVerificationTypes((prev) =>
    prev.map((type) => {
      if (type.id !== selectedTypeId) return type;

      return {
        ...type,
        fields: type.fields.map((field) => ({
          ...field,
          inputType: fieldTypeMap[field.key] || field.inputType,
        })),
      };
    }),
  );

  // Normalize values
  verificationType.fields.forEach((field) => {
    const inputType = fieldTypeMap[field.key] || field.inputType || "text";

    if (
      !normalizedData[field.key] ||
      moment.isMoment(normalizedData[field.key])
    ) {
      return;
    }

    switch (inputType) {
      case "date":
        normalizedData[field.key] = moment(
          normalizedData[field.key],
          "DD-MM-YYYY",
        );
        break;

      case "month":
        normalizedData[field.key] = moment(
          normalizedData[field.key],
          "MM-YYYY",
        );
        break;

      default:
        break;
    }
  });

  return normalizedData;
};
